// Forecasts API service
import { baseClient } from './baseClient';
import type {
  ForecastSummary,
  ForecastDetail
} from '../types/api';

export class ForecastsApi {
  private static instance: ForecastsApi;

  private constructor() {}

  public static getInstance(): ForecastsApi {
    if (!ForecastsApi.instance) {
      ForecastsApi.instance = new ForecastsApi();
    }
    return ForecastsApi.instance;
  }

  /**
   * Get the latest forecast summary
   */
  async getLatestForecast(): Promise<ForecastSummary> {
    return baseClient.get<ForecastSummary>('/forecasts/latest');
  }

  /**
   * Get forecast details by ID
   */
  async getForecastDetails(forecastId: string): Promise<ForecastDetail[]> {
    return baseClient.get<ForecastDetail[]>(`/forecasts/details/${forecastId}`);
  }

  /**
   * Get all forecast summaries
   */
  async getForecastSummaries(): Promise<ForecastSummary[]> {
    return baseClient.get<ForecastSummary[]>('/forecasts/');
  }

  /**
   * Get forecast by ID
   */
  async getForecast(id: string): Promise<ForecastSummary> {
    return baseClient.get<ForecastSummary>(`/forecasts/${id}`);
  }

  /**
   * Create a new forecast
   */
  async createForecast(forecastData: any): Promise<ForecastSummary> {
    return baseClient.post<ForecastSummary>('/forecasts/', forecastData);
  }

  /**
   * Update forecast details
   */
  async updateForecast(id: string, forecastData: any): Promise<ForecastSummary> {
    return baseClient.patch<ForecastSummary>(`/forecasts/${id}`, forecastData);
  }

  /**
   * Delete a forecast
   */
  async deleteForecast(id: string): Promise<void> {
    return baseClient.delete<void>(`/forecasts/${id}`);
  }

  /**
   * Get forecast details for a specific deal
   */
  async getDealForecastDetails(dealId: string): Promise<ForecastDetail[]> {
    return baseClient.get<ForecastDetail[]>(`/forecasts/deals/${dealId}/details`);
  }

  /**
   * Get forecast accuracy metrics
   */
  async getForecastAccuracy(): Promise<any> {
    return baseClient.get<any>('/forecasts/accuracy');
  }

  /**
   * Get forecast trends
   */
  async getForecastTrends(period: string = 'monthly'): Promise<any> {
    return baseClient.get<any>(`/forecasts/trends?period=${period}`);
  }

  /**
   * Get forecast comparison (team vs AI)
   */
  async getForecastComparison(): Promise<any> {
    return baseClient.get<any>('/forecasts/comparison');
  }

  /**
   * Get forecast by date range
   */
  async getForecastsByDateRange(startDate: string, endDate: string): Promise<ForecastSummary[]> {
    const params = new URLSearchParams();
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    
    return baseClient.get<ForecastSummary[]>(`/forecasts/?${params.toString()}`);
  }

  /**
   * Get forecast statistics
   */
  async getForecastStats(): Promise<any> {
    return baseClient.get<any>('/forecasts/stats');
  }

  /**
   * Get risk analysis for deals
   */
  async getDealRiskAnalysis(): Promise<any> {
    return baseClient.get<any>('/forecasts/risk-analysis');
  }

  /**
   * Get forecast recommendations
   */
  async getForecastRecommendations(): Promise<any> {
    return baseClient.get<any>('/forecasts/recommendations');
  }

  /**
   * Export forecast data
   */
  async exportForecast(id: string, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    const response = await baseClient.get<Blob>(`/forecasts/${id}/export?format=${format}`, {
      responseType: 'blob'
    });
    return response;
  }

  /**
   * Get forecast alerts
   */
  async getForecastAlerts(): Promise<any[]> {
    return baseClient.get<any[]>('/forecasts/alerts');
  }

  /**
   * Acknowledge forecast alert
   */
  async acknowledgeForecastAlert(alertId: string): Promise<void> {
    return baseClient.post<void>(`/forecasts/alerts/${alertId}/acknowledge`);
  }

  /**
   * Get forecast pipeline analysis
   */
  async getPipelineAnalysis(): Promise<any> {
    return baseClient.get<any>('/forecasts/pipeline-analysis');
  }

  /**
   * Get forecast by stage
   */
  async getForecastByStage(stage: string): Promise<any> {
    return baseClient.get<any>(`/forecasts/by-stage/${stage}`);
  }

  /**
   * Get forecast by owner
   */
  async getForecastByOwner(ownerId: string): Promise<any> {
    return baseClient.get<any>(`/forecasts/by-owner/${ownerId}`);
  }

  /**
   * Get forecast history
   */
  async getForecastHistory(forecastId: string): Promise<any[]> {
    return baseClient.get<any[]>(`/forecasts/${forecastId}/history`);
  }

  /**
   * Get forecast performance metrics
   */
  async getForecastPerformance(): Promise<any> {
    return baseClient.get<any>('/forecasts/performance');
  }
}

// Export singleton instance
export const forecastsApi = ForecastsApi.getInstance(); 